import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BookOpen,
  Github,
  Activity,
  ShieldAlert,
  CreditCard,
  LogIn,
  Settings2,
  FileText,
  Key,
  Bug,
  Menu,
  X,
} from 'lucide-react';
import { usePageSEO } from '@/hooks/usePageSEO';
import { PUBLIC_PAGES } from '@/lib/seo/public-routes';
import { pageMetadata } from '@/lib/seo/metadata';
import { PublicContainer } from '@/components/layouts/PublicContainer';

const Docs: React.FC = () => {
  usePageSEO(pageMetadata(PUBLIC_PAGES.docs));
  const { hash } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (hash) {
      // Small timeout ensures the DOM has rendered the elements before scrolling
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    }
  }, [hash]);

  const navLinks = [
    { id: 'getting-started', label: 'Getting Started', icon: BookOpen },
    { id: 'authentication', label: 'Authentication', icon: LogIn },
    { id: 'connecting-github', label: 'Connecting GitHub', icon: Github },
    { id: 'running-scans', label: 'Running Scans', icon: Activity },
    { id: 'understanding-metrics', label: 'Understanding Metrics', icon: ShieldAlert },
    { id: 'issues-and-remediation', label: 'Issues & AI Remediation', icon: Bug },
    { id: 'integrations', label: 'Jira & Trello', icon: Settings2 },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'enterprise-saml', label: 'Enterprise SAML', icon: Key },
    { id: 'billing', label: 'Billing & Teams', icon: CreditCard },
  ];

  return (
    <>
      {/* Mobile collapsible TOC — hidden on md+ */}
      <div className="md:hidden sticky top-16 z-10 bg-background border-b border-border px-4 py-2">
        <button
          onClick={() => setMobileNavOpen(o => !o)}
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
          aria-expanded={mobileNavOpen}
        >
          {mobileNavOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          {mobileNavOpen ? 'Close' : 'Contents'}
        </button>
        {mobileNavOpen && (
          <nav aria-label="Contents" className="mt-2 flex flex-col gap-1 pb-2">
            {navLinks.map(link => {
              const Icon = link.icon;
              return (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={() => setMobileNavOpen(false)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {link.label}
                </a>
              );
            })}
          </nav>
        )}
      </div>

      <PublicContainer className="py-12 lg:py-16 flex flex-col md:flex-row gap-12 pt-8 md:pt-16">
      {/* Sidebar Navigation */}
      <aside className="hidden md:block w-64 shrink-0">
        <div className="sticky top-28 bg-surface-1 border border-hairline rounded-2xl shadow-sm p-6 overflow-y-auto max-h-[calc(100vh-8rem)]">
          <h2 className="mono text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-4">Table of Contents</h2>
          <nav aria-label="Table of contents" className="space-y-2">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface-2 transition-colors"
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate">{link.label}</span>
                </a>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Main Content Area. A plain div: PublicLayout owns the page's single
          main landmark, and nesting a second one is invalid. */}
      <div className="flex-1 max-w-4xl min-w-0">
        <div className="space-y-16 pb-32">
          {/* Getting Started */}
          <section id="getting-started" className="scroll-mt-28">
            <h1 className="text-4xl font-black tracking-tight text-foreground mb-6">Documentation</h1>
            <p className="text-lg leading-relaxed text-muted-foreground mb-4">
              Welcome to the DebtDrone documentation. DebtDrone is an automated technical debt and code complexity scanner designed to run seamlessly alongside your development workflows. By continuously analyzing your repositories, we pinpoint actionable hotspots so your team can tackle technical debt before it becomes a liability.
            </p>
            <p className="text-lg leading-relaxed text-muted-foreground">
              The platform operates by parsing your source code into Abstract Syntax Trees (AST) to measure cyclomatic complexity and using powerful industry-standard security scanners (like Trivy) to identify vulnerabilities. All of this is presented in a centralized dashboard for engineering leaders to make informed prioritization decisions.
            </p>
          </section>

          {/* Authentication */}
          <section id="authentication" className="scroll-mt-28">
            <h2 className="text-2xl font-bold text-foreground mb-4">Authentication & Accounts</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed text-base">
              <p>
                DebtDrone supports three ways to sign in: standard email and password, one-click <strong>Google OAuth</strong>, or <strong>GitHub OAuth</strong>. All three work for both signing up and logging back in.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">Account Workflows</h3>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Email Verification:</strong> If registering with email/password, your password must include an uppercase letter, a lowercase letter, a number, and a special character (8+ characters total). After registering, we email a 6-digit verification code — it expires after 10 minutes, and you get 5 attempts to enter it correctly before needing to restart signup.
                </li>
                <li>
                  <strong>Password Security:</strong> Passwords are securely hashed, never stored in plain text. If you lose access, the "Forgot Password" flow emails a secure, time-limited reset link (valid for 1 hour). Resetting your password automatically signs you out of every other active session for security.
                </li>
                <li>
                  <strong>Profile Management:</strong> Inside the Dashboard, navigate to <strong>Profile</strong> to update your display name, avatar, and account details.
                </li>
                <li>
                  <strong>Impersonation (Admins only):</strong> System administrators have the ability to securely impersonate users for troubleshooting. When active, an unmistakable yellow banner is displayed across the screen to indicate the impersonated state.
                </li>
              </ul>
            </div>
          </section>

          {/* Connecting GitHub */}
          <section id="connecting-github" className="scroll-mt-28">
             <h2 className="text-2xl font-bold text-foreground mb-4">Connecting GitHub</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed text-base">
              <p>
                Getting a repository fully scanning takes two steps: connecting it to DebtDrone, and installing the DebtDrone GitHub App.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">1. Connect your repositories</h3>
              <p>
                In <strong>Settings → Integrations</strong>, connect your GitHub account via OAuth and select which repositories you want DebtDrone to track. You can re-sync at any time to pick up newly created repositories.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">2. Install the GitHub App</h3>
              <p>
                Connecting a repository lets DebtDrone see it, but the GitHub App itself must also be installed on your account or organization for scans to run automatically and for pull request comments to appear. If it isn't installed yet, you'll see an <strong>"Install on GitHub"</strong> button on your Dashboard — click it to be taken straight to GitHub's install page. Once installed, the button disappears automatically.
              </p>

              <div className="bg-surface-2 border border-hairline rounded-xl p-6 mt-4">
                <h3 className="text-sm font-semibold text-foreground mb-2">Security Architecture & Permissions</h3>
                <p className="text-sm mb-2">
                  The DebtDrone GitHub App requests read and write access to repository contents and pull requests, plus read access to metadata. Write access is what allows the optional AI Remediation feature to open fix pull requests directly against your repository — you'll see the exact requested scopes on GitHub's own install-confirmation screen before approving.
                </p>
                <p className="text-sm">
                  Your source code is analyzed inside isolated, ephemeral worker containers — we don't maintain a persistent clone of your repository outside of what's needed to run a scan.
                </p>
              </div>
            </div>
          </section>

          {/* Running Scans */}
          <section id="running-scans" className="scroll-mt-28">
            <h2 className="text-2xl font-bold text-foreground mb-4">Running & Managing Scans</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed text-base">
              <p>
                After connecting your repositories, they appear in the <strong>Repositories</strong> table. Each repository displays its last scan status, active issues count, and historical debt score.
              </p>
              
              <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">The Scanning Lifecycle</h3>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Triggering:</strong> Click the <strong>Trigger Scan</strong> button, or open a pull request on a repository with the GitHub App installed — either way, a job is queued for processing.
                </li>
                <li>
                  <strong>Queued:</strong> The UI will update to indicate the repository is "Pending".
                </li>
                <li>
                  <strong>Processing:</strong> A background worker picks up the job, fetches the codebase, and begins passing files through the complexity engine and security scanner (Trivy). Depending on the size of your monorepo, a scan can take 10 seconds to 15 minutes.
                </li>
                <li>
                  <strong>Completion:</strong> Once finished, the interface instantly updates via polling, populating the new issues list and recalculating your dashboard metrics.
                </li>
              </ol>
              <p className="mt-2">
                When a pull request is scanned, DebtDrone posts a single, continuously-updated comment summarizing the health grade and any new issues, and sets a GitHub commit status on the PR — which you can require as a passing check in your branch protection rules. DebtDrone never blocks a merge on its own; it only reports.
              </p>
            </div>
          </section>

          {/* Understanding Metrics */}
          <section id="understanding-metrics" className="scroll-mt-28">
            <h2 className="text-2xl font-bold text-foreground mb-4">Understanding Your Metrics</h2>
            <div className="space-y-6 text-muted-foreground leading-relaxed text-base">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Debt Score & Health Grade</h3>
                <p>
                  The Health Grade is a holistic letter grade from A (excellent) to F (critical), calculated from your repository's open issues — critical security vulnerabilities weigh the grade down fastest, while accumulated complexity erodes it gradually.
                  Tracking this grade over time helps visualize whether your refactoring velocity is outpacing new feature development.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Actionable Hotspots</h3>
                <p>
                  Technical debt isn't equally dangerous. An ugly, untouched utility script is far less risky than a complex file modified daily.
                  Our engine correlates <strong>Code Churn</strong> (commit frequency from git history) with <strong>Cyclomatic Complexity</strong>.
                  Files ranking highest on both axes are surfaced natively on the dashboard as the highest priority "Actionable Hotspots."
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Test Coverage (Beta)</h3>
                <p>
                  Organizations can optionally enable coverage tracking by uploading the coverage report your CI pipeline already produces (lcov, Cobertura, JaCoCo, Clover, SimpleCov, and Go coverage formats are all supported). DebtDrone does not run your test suite. Without an uploaded report there is no coverage data for a repository — read that as unavailable, not 0%. Some views still display a missing report as 0.0% while we fix that. This feature is opt-in and still evolving — ask your admin to enable it under organization settings if you'd like to try it.
                </p>
              </div>
              <div className="bg-sev-medium/10 border border-sev-medium/20 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-sev-medium mb-2">Intelligent Parsing & System Warnings</h3>
                <p className="text-sm text-sev-medium/90">
                  Massive auto-generated files natively distort analytics. DebtDrone uses heuristic detection to silently skip files like bundled JavaScript, <code>package-lock.json</code>, large JSON payloads, and generated protobuf/GraphQL definitions. This guarantees your metrics reflect strictly human-written code.
                </p>
              </div>
            </div>
          </section>

          {/* Issues & Remediation */}
          <section id="issues-and-remediation" className="scroll-mt-28">
            <h2 className="text-2xl font-bold text-foreground mb-4">Issues & AI Remediation</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed text-base">
              <p>
                The <strong>Issues</strong> tab serves as your technical backlog. Scans categorize findings into <strong>Security Vulnerabilities</strong> (e.g., exposed secrets, CVEs in dependencies) and <strong>Code Quality Issues</strong> (e.g., function too complex, duplicated blocks).
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">Granular Code Views</h3>
              <p>
                Clicking any issue opens the <strong>Issue Detail View</strong>. This interface provides the exact file path, the severity level, and a syntax-highlighted code snippet displaying the precise lines affected. You can mark issues as "Ignored" if they represent a false positive, filtering them permanently from future severity calculations.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">AI-Powered Remediation (Pro Feature)</h3>
              <p>
                For our Pro and Enterprise users, DebtDrone transcends static analysis. When viewing an eligible issue, click <strong>"Enable AI Remediation"</strong>.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>DebtDrone securely contextualizes the affected code and sends it to a large language model.</li>
                <li>The engine proposes an actionable refactoring fix (e.g., extracting interfaces, abstracting logic, or mitigating a vulnerability).</li>
                <li>You review the proposed fix before it's opened as a pull request against your GitHub repository.</li>
                <li>Merging that pull request automatically marks the originating issue as resolved.</li>
              </ul>
            </div>
          </section>

          {/* Integrations */}
          <section id="integrations" className="scroll-mt-28">
            <h2 className="text-2xl font-bold text-foreground mb-4">Project Management Integrations</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed text-base">
              <p>
                We know developers hate context switching. DebtDrone bridges the gap between infrastructure health and project management by converting identified debt into trackable agile tickets directly from the UI.
              </p>
              
              <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">Configuring External Trackers</h3>
              <ul className="list-disc pl-5 space-y-4">
                <li>
                  <strong>Atlassian Jira:</strong> Navigate to <code>Settings -&gt; Integrations</code>. Enter your Jira API Token, Atlassian Email, and Base URL. Once connected, a "Push to Jira" button appears on all issues. You can dynamically select the target Jira Project and customize the Issue Type (Task, Bug, Epic), directly syncing the vulnerability description to the Jira card.
                </li>
                <li>
                  <strong>Trello Configuration:</strong> Similarly, supply your Trello API Key and User Token. DebtDrone maps your Trello Workspaces; you can explicitly configure which Trello Board and target Column debt tickets should automatically populate upon synchronizing.
                </li>
              </ul>

              <div className="bg-lime/10 border border-lime/20 rounded-xl p-6 mt-4">
                <p className="text-sm text-foreground/90">
                  <strong>Note:</strong> Jira and Trello integrations are gated to our <strong>Pro</strong> and <strong>Enterprise</strong> tiers.
                </p>
              </div>
            </div>
          </section>

          {/* Reports */}
          <section id="reports" className="scroll-mt-28">
            <h2 className="text-2xl font-bold text-foreground mb-4">Reports</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed text-base">
              <p>
                Engineers solve problems at the repository level, but Engineering Managers and CTOs need macro visibility. The <strong>Reports</strong> tab is your organization's full scan history — every analysis run across every connected repository, in one table.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">What you'll see</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Health grade per run:</strong> Each scan is stamped with an A–F grade, so you can spot regressions at a glance.</li>
                <li><strong>Trend indicators:</strong> See whether a repository's debt is trending up or down run-over-run.</li>
                <li><strong>Run detail drill-down:</strong> Click into any run to see exactly what changed and which issues were introduced or resolved.</li>
              </ul>
              <p>
                Reports is part of our advanced analytics feature set, available on Pro and Enterprise plans.
              </p>
            </div>
          </section>

          {/* Enterprise SAML */}
          <section id="enterprise-saml" className="scroll-mt-28">
             <h2 className="text-2xl font-bold text-foreground mb-4">Enterprise SAML Configuration</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed text-base">
              <p>
                For organizations enforcing strict access boundaries, DebtDrone's <strong>Enterprise</strong> plan includes SAML 2.0 Single Sign-On, letting your team authenticate through your company's Identity Provider (IdP) — Okta, Google Workspace, Azure AD, JumpCloud, and others.
              </p>
              <p>
                Because SSO setup involves securely exchanging metadata with your IdP, our team configures it for you rather than asking you to do it yourself. Once you're on the Enterprise plan, reach out to our support team and we'll get your organization connected.
              </p>
            </div>
          </section>

          {/* Billing */}
          <section id="billing" className="scroll-mt-28">
            <h2 className="text-2xl font-bold text-foreground mb-4">Billing & Organization Management</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed text-base">
              <p>
                DebtDrone operates on an Organizational model. An Organization encapsulates all connected Repositories, Teams, billing details, and customized settings securely partitioned from other users.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">Role-Based Access Control (RBAC)</h3>
              <p>
                Invite team members via the Settings panel and assign one of four organizational roles:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Owner:</strong> Full control, including billing and the ability to delete the organization.</li>
                <li><strong>Admin:</strong> Can manage billing, invite members, and configure integrations.</li>
                <li><strong>Member:</strong> Can trigger scans, view issues, and use configured integrations.</li>
                <li><strong>Viewer:</strong> Strictly read-only access for browsing issues and reports.</li>
              </ul>

              <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">Plans</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Free:</strong> Up to 3 repositories, 10 scans per month.</li>
                <li><strong>Pro ($29/mo):</strong> Up to 100 repositories, unlimited scans, plus Jira/Trello integrations, AI Remediation, and advanced analytics (Reports).</li>
                <li><strong>Enterprise:</strong> Unlimited repositories and scans, custom integrations, and SAML SSO.</li>
              </ul>
              <p>
                We use Paddle for secure checkout and subscription management — access the <strong>Billing</strong> tab in Settings to upgrade or change your plan at any time.
              </p>
            </div>
          </section>
        </div>
      </div>
      </PublicContainer>
    </>
  );
};

export default Docs;
